import { Card, ReferenceCard } from "@/src/components/cards";
import { closeDrawer, openDrawer } from "@/src/components/dialogs";
import { InfiniteFetchableList } from "@/src/components/fetchableLists";
import { SinglePicker } from "@/src/components/singlePicker";
import {
  FrequentSnomedParent,
  frequentSnomedParents,
  Reference,
  ReferenceCategory,
  SnomedConcept,
} from "@/src/data/allTypes";
import apiFetch from "@/src/utils/apiFetch";
import { getItemId, uid } from "@/src/utils/utils";
import { useState } from "react";
import SnomedCategoryName from "./snomedCategoryName";
import SnomedConceptCard from "./snomedConceptCard";
import { USER } from "@/src/data/globalVariables";
import SearchBar from "@/src/components/searchBar";

export function SnomedConceptPicker({
  parentIds,
  value,
  onChange,
}: {
  parentIds?: FrequentSnomedParent[];
  value: SnomedConcept | null | undefined;
  onChange: (newValue: SnomedConcept | null | undefined) => void;
}) {
  return (
    <SinglePicker
      title="Concept SNOMED"
      queryKey={["snomedConcepts"]}
      loadItems={async (limit, offset, searchKey) => {
        return await apiFetch("snomedConcepts", "loadMany", {
          searchKey,
          limit,
          offset,
        });
      }}
      value={value}
      onChange={(newValue) => {
        onChange(newValue);
      }}
      getChecboxCardContent={(item) => {
        return (
          <SnomedConceptCard
            concept={item}
            searchKey={""}
            filteredParentIds={parentIds}
          />
        );
      }}
      getChild={(item) => {
        return (
          <Card
            color="blue"
            title={item?.name}
            subTitle={
              <SnomedCategoryName
                concept={item}
                filteredParentIds={parentIds}
              />
            }
          ></Card>
        );
      }}
    ></SinglePicker>
  );
}

export function SnomedConceptsList({
  parentIds,
  currentSearchKey,
  showSearchBar,
  category,
  onSelect,
}: {
  parentIds?: FrequentSnomedParent[];
  currentSearchKey?: string;
  showSearchBar?: boolean;
  category: ReferenceCategory;
  onSelect: (reference: Reference) => void;
}) {
  const [reload, setReload] = useState(0);
  const [searchKey, setSearchKey] = useState(currentSearchKey || "");

  return (
    <div className="grid c1 gap10">
      {showSearchBar != false && (
        <SearchBar
          value={searchKey}
          placeholder="Rechercher un élément..."
          onChange={(newValue) => {
            setSearchKey(newValue || "");
          }}
        ></SearchBar>
      )}
      <div className="gray">
        {searchKey == "" && "Eléments les plus fréquents"}
      </div>
      {searchKey ? (
        <InfiniteFetchableList
          queryKey={[
            "SnomedMotivesList",
            searchKey,
            reload,
            JSON.stringify(parentIds),
            category,
          ]}
          limit={40}
          getFunction={(limit, offset, searchKey) =>
            apiFetch(
              "snomedConcepts",
              "loadMany",
              {
                searchKey,
                limit,
                offset,
                parentIds: parentIds?.map(
                  (parent) => frequentSnomedParents[parent].id
                ),
              },
              "Erreur lors du chargement des motifs"
            )
          }
          searchKey={searchKey}
          emptyChild={
            <div className="grid c1 gap10">
              {searchKey.length < 3 ? (
                <div className=" p20 tcenter">
                  Veuillez saisir au moins <strong>3 lettres</strong>
                </div>
              ) : (
                <div className="gray p20 center">
                  Aucun élément trouvé
                  <strong>{searchKey && `content ${searchKey}`}</strong>
                </div>
              )}
            </div>
          }
        >
          {(data) => (
            <div className="grid c1 gap10">
              {data?.map((concept) => {
                return (
                  <SnomedConceptCard
                    key={uid()}
                    filteredParentIds={parentIds}
                    searchKey={searchKey}
                    concept={concept}
                    onClick={async () => {
                      if (!concept.id) return;
                      const reference = await apiFetch(
                        "references",
                        "upsertUsingSnomedConcept",
                        {
                          conceptId: concept.id,
                          category,
                          postCoordinationConceptIds: [],
                        }
                      );
                      onSelect(reference);
                    }}
                  />
                );
              })}
            </div>
          )}
        </InfiniteFetchableList>
      ) : (
        <InfiniteFetchableList
          queryKey={[
            "FrequentReferencesList",
            searchKey,
            reload,
            JSON.stringify(parentIds),
            category,
          ]}
          limit={40}
          searchKey={searchKey}
          getFunction={(limit, offset, searchKey) => {
            let frequentReferences =
              USER?.get()
                ?.speciality?.frequentReferences?.filter(
                  (reference) => reference?.category == category
                )
                .filter((frequentReference) => frequentReference != null) || [];
            async function getReferences() {
              return frequentReferences;
            }
            if (frequentReferences.length) return getReferences();
            return apiFetch(
              "references",
              "loadMany",
              {
                searchKey,
                limit,
                offset,
                categoriesIds: category ? [category] : undefined,
                showFrequentOnly: true,
              },
              "Erreur lors du chargement des motifs"
            );
          }}
          emptyChild={
            <div className="grid c1 gap10">
              <div className="gray p20 center">
                Aucun élément trouvé
                <strong>{searchKey && `content ${searchKey}`}</strong>
              </div>
            </div>
          }
        >
          {(data) => {
            return (
              <div className="c1 gap10">
                {data?.map((reference) => {
                  return (
                    <ReferenceCard
                      key={uid()}
                      searchKey={searchKey}
                      reference={reference}
                      onClick={async () => {
                        let snomedConceptId = getItemId(
                          reference.snomedConcept
                        );
                        if (getItemId(reference.snomedConcept))
                          reference = await apiFetch(
                            "references",
                            "upsertUsingSnomedConcept",
                            {
                              conceptId: snomedConceptId,
                              category,
                              postCoordinationConceptIds: [],
                            }
                          );
                        onSelect(reference);
                      }}
                    />
                  );
                })}
              </div>
            );
          }}
        </InfiniteFetchableList>
      )}
    </div>
  );
}

export async function openSnomedConceptPicker({
  title,
  parentIds,
  category,
  onSelect,
}: {
  title: string;
  parentIds?: FrequentSnomedParent[];
  category: ReferenceCategory;
  onSelect: (reference: Reference) => void;
}) {
  await openDrawer({
    id: "snomedConceptPickerDrawer",
    title,
    additionalClasses: ["small"],
    content: (
      <SnomedConceptsList
        parentIds={parentIds}
        category={category}
        onSelect={(reference) => {
          onSelect(reference);
          closeDrawer("snomedConceptPickerDrawer");
        }}
      ></SnomedConceptsList>
    ),
  });
}
